import { useAuth } from '../app/Auth';
import { Chip, Empty, ErrorNote, Icon, PageHeader, SectionTitle, Segmented, Spinner } from '../app/ui';
import { usePref } from '../lib/persist';
import type { OrgChart } from '../lib/org';
import { OrgView } from './Org';
import { ageLabel } from '../lib/format';
import { useApi } from '../lib/useApi';
import type { SessionRow } from '../lib/types';

export function SessionCard({ s }: { s: SessionRow }) {
  const tone = s.needsErwin ? 'orange' : s.state === 'blocked' ? 'red' : 'green';
  return (
    <article className="card session">
      <div className="row wrap gap">
        <strong>{s.title || `${s.tool ?? 'session'} session`}</strong>
        <Chip tone={tone}>{s.needsErwin ? 'needs you' : s.state ?? 'active'}</Chip>
        <span className="spacer" />
        <span className="muted small">{ageLabel(s.lastSeen)}</span>
      </div>
      <div className="row wrap gap muted small">
        {s.tool ? <span>{s.tool}</span> : null}
        {s.projectName ? <span>{s.projectName}</span> : null}
        {s.branch ? <span className="mono">{s.branch}</span> : null}
        {s.model ? <span>{s.model}</span> : null}
        {s.host ? <span>{s.host}</span> : null}
      </div>
      {s.lastEvent?.summary ? (
        <p className="event"><Chip>{s.lastEvent.kind ?? 'event'}</Chip> {s.lastEvent.summary} <span className="muted small">{ageLabel(s.lastEvent.at)}</span></p>
      ) : null}
    </article>
  );
}

export function NowPage() {
  const { refreshMe } = useAuth();
  const q = useApi<{ sessions: SessionRow[] }>('sessions_now', {}, { poll: 30000 });
  const sessions = q.data?.sessions ?? [];
  const [mode, setMode] = usePref<'org' | 'sessions'>('now.mode', 'org');
  const org = useApi<OrgChart>('org_chart', {}, { poll: 60000 });
  return (
    <section className="page">
      <PageHeader title="Happening now">
        <button className="icon-btn" onClick={() => { void q.reload(); void org.reload(); void refreshMe(); }} aria-label="Refresh"><Icon name="refresh" /></button>
      </PageHeader>
      <Segmented label="Happening now view" value={mode} onChange={setMode} options={[{ id: 'org', label: 'Org' }, { id: 'sessions', label: `Sessions (${sessions.length})` }]} />
      {mode === 'org' ? (
        <>
          {org.loading && !org.data ? <Spinner label="Loading…" /> : null}
          <OrgView org={org.data} />
        </>
      ) : (
        <>
          <ErrorNote text={q.error} />
          <SectionTitle count={sessions.length}>Sessions in the last 30 minutes, blocked, or needing you</SectionTitle>
          {q.loading && !q.data ? <Spinner label="Loading…" /> : null}
          {q.data && !sessions.length ? <Empty>Quiet.</Empty> : null}
          <div className="stack">{sessions.map((s) => <SessionCard key={s.id} s={s} />)}</div>
        </>
      )}
    </section>
  );
}
